import React, { useCallback, useEffect, useState } from "react";
import {
  Loader2, Ambulance, Phone, MapPin, Clock, GripVertical, User, DollarSign,
  MoreHorizontal, Map as MapIcon, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { listDispatch, updateDispatch, getStaffAvailability, DISPATCH_LANES } from "@/lib/api";
import DispatchMap from "@/components/dispatch/DispatchMap";
import DispatchJobDialog from "@/components/dispatch/DispatchJobDialog";
import StaffAssignmentPanel from "@/components/dispatch/StaffAssignmentPanel";

const URGENCY_TINTS = {
  Immediate: { bg: "rgba(155,28,28,0.10)", color: "#9B1C1C" },
  "Same Day": { bg: "rgba(212,175,55,0.18)", color: "#7A5E12" },
  Standard: { bg: "#F1F5F9", color: "#475569" },
};

const PAYMENT_TINTS = {
  paid: { bg: "rgba(15,109,93,0.12)", color: "#0F6D5D", label: "Paid" },
  partial_payment: { bg: "rgba(212,175,55,0.18)", color: "#7A5E12", label: "Partial" },
  unpaid: { bg: "rgba(155,28,28,0.10)", color: "#9B1C1C", label: "Unpaid" },
};

const DRAG_MIME = "application/x-tj-dispatch-card";
const POLL_MS = 10_000;

export default function DispatchPanel({ onSessionExpired }) {
  const [loading, setLoading] = useState(true);
  const [lanes, setLanes] = useState({});
  const [counts, setCounts] = useState({});
  const [pins, setPins] = useState([]);
  const [dragOver, setDragOver] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const [staff, setStaff] = useState([]);
  const [staffLoading, setStaffLoading] = useState(true);

  const [activeJobId, setActiveJobId] = useState(null);
  const [mapVisible, setMapVisible] = useState(true);

  const fetchAll = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const data = await listDispatch();
      setLanes(data.lanes || {});
      setCounts(data.counts || {});
      setPins(data.pins || []);
    } catch (err) {
      if (err?.response?.status === 401) { onSessionExpired?.(); return; }
      if (!silent) toast.error("Failed to load dispatch board.");
    } finally {
      if (!silent) setLoading(false);
    }
  }, [onSessionExpired]);

  const fetchStaff = useCallback(async (silent = false) => {
    if (!silent) setStaffLoading(true);
    try {
      const data = await getStaffAvailability();
      setStaff(data.staff || []);
    } catch (err) {
      if (err?.response?.status === 401) { onSessionExpired?.(); return; }
    } finally {
      if (!silent) setStaffLoading(false);
    }
  }, [onSessionExpired]);

  // Initial fetch on mount
  useEffect(() => {
    fetchAll(false);
    fetchStaff(false);
  }, [fetchAll, fetchStaff]);

  // Poll every 10s in the background — PAUSED while a job dialog is open
  // so the dialog doesn't flicker / reload while an admin is editing.
  useEffect(() => {
    if (activeJobId) return;
    const t = setInterval(() => {
      fetchAll(true);
      fetchStaff(true);
    }, POLL_MS);
    return () => clearInterval(t);
  }, [fetchAll, fetchStaff, activeJobId]);

  const moveCard = async (cardId, fromLane, toLane) => {
    if (fromLane === toLane) return;
    const card = (lanes[fromLane] || []).find((c) => c.id === cardId);
    if (!card) return;
    setBusyId(cardId);
    const toCompleted = toLane === "completed";
    setLanes((prev) => {
      const out = { ...prev };
      out[fromLane] = (out[fromLane] || []).filter((c) => c.id !== cardId);
      if (!toCompleted) {
        out[toLane] = [{ ...card, dispatch_status: toLane }, ...(out[toLane] || [])];
      }
      return out;
    });
    setCounts((p) => ({
      ...p,
      [fromLane]: Math.max(0, (p[fromLane] || 1) - 1),
      [toLane]: toCompleted ? (p[toLane] || 0) : (p[toLane] || 0) + 1,
    }));
    try {
      await updateDispatch(cardId, { dispatch_status: toLane });
      const laneMeta = DISPATCH_LANES.find((l) => l.value === toLane);
      toast.success(toCompleted
        ? `Job completed — removed from board`
        : `Moved to ${laneMeta?.label || toLane}`);
      fetchStaff(true);
    } catch (err) {
      toast.error("Failed to move. Reverting.");
      fetchAll();
    } finally {
      setBusyId(null);
    }
  };

  const onCardDragStart = (e, card, fromLane) => {
    e.dataTransfer.setData(DRAG_MIME, JSON.stringify({ id: card.id, fromLane }));
    e.dataTransfer.effectAllowed = "move";
    try { e.currentTarget.style.opacity = "0.5"; } catch {}
  };
  const onCardDragEnd = (e) => { try { e.currentTarget.style.opacity = "1"; } catch {} };
  const onLaneDragOver = (e, laneValue) => {
    if (e.dataTransfer.types.includes(DRAG_MIME)) {
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      setDragOver(laneValue);
    }
  };
  const onLaneDragLeave = (e, laneValue) => {
    if (dragOver === laneValue) setDragOver(null);
  };
  const onLaneDrop = (e, toLane) => {
    e.preventDefault();
    setDragOver(null);
    const raw = e.dataTransfer.getData(DRAG_MIME);
    if (!raw) return;
    try {
      const { id, fromLane } = JSON.parse(raw);
      if (id && fromLane && fromLane !== toLane) moveCard(id, fromLane, toLane);
    } catch { /* ignore */ }
  };

  const handleJobSaved = () => {
    fetchAll(true);
    fetchStaff(true);
  };

  const totalCount = Object.values(counts).reduce((a, b) => a + b, 0);

  return (
    <div className="space-y-5" data-testid="dispatch-panel">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] font-medium mb-1" style={{ color: "#7A5E12" }}>
            Operations
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold" style={{ color: "#0B2545" }}>
            Dispatch Center
          </h1>
          <p className="mt-1 text-sm max-w-xl" style={{ color: "#64748B" }}>
            Drag cards between lanes to advance jobs, or click a card to assign staff,
            geocode addresses, edit service-specific details, and resolve scheduling conflicts.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setMapVisible((v) => !v)}
                  data-testid="toggle-map">
            <MapIcon className="h-3.5 w-3.5 mr-1.5" />
            {mapVisible ? "Hide map" : "Show map"}
          </Button>
          <Button variant="outline" size="sm" onClick={() => { fetchAll(); fetchStaff(); }}
                  data-testid="dispatch-refresh">
            <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh
          </Button>
        </div>
      </div>

      {/* Map */}
      {mapVisible && (
        <DispatchMap
          pins={pins}
          height={340}
          onSelect={(id) => setActiveJobId(id)}
        />
      )}

      {/* Board + Staff sidebar */}
      <div className="grid grid-cols-1 xl:grid-cols-[1fr_320px] gap-4">
        <div>
          {loading ? (
            <div className="grid place-items-center py-20">
              <Loader2 className="h-6 w-6 animate-spin" style={{ color: "#0B2545" }} />
            </div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="dispatch-board">
              {DISPATCH_LANES.map((lane) => {
                const isOver = dragOver === lane.value;
                return (
                  <div
                    key={lane.value}
                    className="rounded-2xl border p-3 flex flex-col transition-colors"
                    style={{
                      background: isOver ? "rgba(212,175,55,0.10)" : "#FFFFFF",
                      borderColor: isOver ? "#D4AF37" : "#E2E8F0",
                      borderStyle: isOver ? "dashed" : "solid",
                      minHeight: 460,
                    }}
                    data-testid={`lane-${lane.value}`}
                    onDragOver={(e) => onLaneDragOver(e, lane.value)}
                    onDragLeave={(e) => onLaneDragLeave(e, lane.value)}
                    onDrop={(e) => onLaneDrop(e, lane.value)}
                  >
                    <div className="flex items-center justify-between mb-3 px-1">
                      <span className="text-xs font-semibold uppercase tracking-wider"
                            style={{ color: lane.color }}>{lane.label}</span>
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full"
                            style={{ background: lane.bg, color: lane.color }}>
                        {counts[lane.value] || 0}
                      </span>
                    </div>
                    <div className="space-y-2 overflow-y-auto pr-1 flex-1" style={{ maxHeight: 680 }}>
                      {(lanes[lane.value] || []).length === 0 ? (
                        <div className="text-[11px] text-center py-6 rounded-lg border-2 border-dashed"
                             style={{ color: "#94A3B8", borderColor: "#F1F5F9" }}>
                          Drop here
                        </div>
                      ) : (
                        (lanes[lane.value] || []).map((card) => (
                          <EnhancedCard
                            key={card.id}
                            card={card}
                            laneValue={lane.value}
                            busy={busyId === card.id}
                            onDragStart={onCardDragStart}
                            onDragEnd={onCardDragEnd}
                            onOpen={() => setActiveJobId(card.id)}
                          />
                        ))
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {totalCount === 0 && !loading && (
            <div className="rounded-2xl border-2 border-dashed py-10 text-center mt-4"
                 style={{ borderColor: "#E2E8F0", color: "#64748B" }}>
              <Ambulance className="h-8 w-8 mx-auto mb-3" style={{ color: "#94A3B8" }} />
              <p className="font-medium" style={{ color: "#0B2545" }}>No active dispatch jobs</p>
              <p className="text-sm mt-1">Incoming service requests will appear in the Pending lane.</p>
            </div>
          )}
        </div>

        <StaffAssignmentPanel
          staff={staff}
          loading={staffLoading}
          onRefresh={() => fetchStaff()}
          onSelectJob={(id) => setActiveJobId(id)}
          onSessionExpired={onSessionExpired}
        />
      </div>

      <DispatchJobDialog
        open={!!activeJobId}
        leadId={activeJobId}
        staff={staff}
        onClose={() => setActiveJobId(null)}
        onSaved={handleJobSaved}
        onSessionExpired={onSessionExpired}
      />
    </div>
  );
}

const EnhancedCard = ({ card, laneValue, busy, onDragStart, onDragEnd, onOpen }) => {
  const tint = URGENCY_TINTS[card.urgency] || URGENCY_TINTS.Standard;
  const pay = PAYMENT_TINTS[card.payment_status] || PAYMENT_TINTS.unpaid;

  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, card, laneValue)}
      onDragEnd={onDragEnd}
      onClick={onOpen}
      className="rounded-xl border p-2.5 bg-white cursor-pointer active:cursor-grabbing select-none transition-shadow hover:shadow-md"
      style={{ borderColor: "#E2E8F0", opacity: busy ? 0.5 : 1 }}
      data-testid={`dispatch-card-${card.id}`}
    >
      <div className="flex items-start gap-1.5">
        <GripVertical className="h-3.5 w-3.5 mt-0.5 shrink-0" style={{ color: "#94A3B8" }} />
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 flex-wrap">
                {card.request_id && (
                  <span className="text-[9px] font-mono font-bold px-1 py-0.5 rounded"
                        style={{ background: "rgba(212,175,55,0.18)", color: "#7A5E12" }}
                        data-testid={`card-reqid-${card.id}`}>
                    {card.request_id}
                  </span>
                )}
                {card.pipeline_stage && (
                  <span className="text-[9px] uppercase tracking-wider px-1 py-0.5 rounded"
                        style={{ background: "rgba(11,37,69,0.06)", color: "#0B2545" }}
                        data-testid={`card-stage-${card.id}`}>
                    {card.pipeline_stage}
                  </span>
                )}
              </div>
              <div className="text-xs font-semibold truncate mt-0.5" style={{ color: "#0B2545" }}>
                {card.full_name}
              </div>
              <div className="text-[10px] uppercase tracking-wider mt-0.5 truncate" style={{ color: "#64748B" }}>
                {(card.services || []).slice(0, 2).join(", ")}
              </div>
            </div>
            <div className="flex flex-col items-end gap-0.5 shrink-0">
              <span className="text-[9px] uppercase font-medium px-1.5 py-0.5 rounded"
                    style={{ background: tint.bg, color: tint.color }}>{card.urgency}</span>
              <span className="text-[9px] uppercase font-medium px-1.5 py-0.5 rounded"
                    style={{ background: pay.bg, color: pay.color }}
                    data-testid={`card-pay-${card.id}`}>
                {pay.label}
              </span>
            </div>
          </div>
          <div className="mt-2 space-y-0.5 text-[10px]" style={{ color: "#475569" }}>
            {card.assigned_staff_name && (
              <div className="flex items-center gap-1" data-testid={`card-staff-${card.id}`}
                   style={{ color: "#0F6D5D" }}>
                <User className="h-2.5 w-2.5" />
                <span className="font-medium">{card.assigned_staff_name}</span>
              </div>
            )}
            {card.phone && (
              <div className="flex items-center gap-1">
                <Phone className="h-2.5 w-2.5" />{card.phone}
              </div>
            )}
            {(card.appointment_at || card.preferred_date) && (
              <div className="flex items-center gap-1">
                <Clock className="h-2.5 w-2.5" />
                {card.appointment_at
                  ? card.appointment_at.slice(0, 16).replace("T", " ")
                  : `${card.preferred_date}${card.preferred_time ? ` · ${card.preferred_time}` : ""}`}
              </div>
            )}
            {card.service_address && (
              <div className="flex items-start gap-1">
                <MapPin className="h-2.5 w-2.5 mt-0.5 shrink-0" />
                <span className="line-clamp-2">{card.service_address}</span>
              </div>
            )}
            {typeof card.amount_due === "number" && card.amount_due > 0 && (
              <div className="flex items-center gap-1">
                <DollarSign className="h-2.5 w-2.5" />
                ${card.amount_due}{card.amount_paid ? ` · paid $${card.amount_paid}` : ""}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
